import { Fragment, useMemo, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../common/TableActionsMenu';
import { NaverRevenueStats, NaverProviderCampaign, getNaverCampaignLabel } from '../../../types/naver.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: NaverRevenueStats;
  providerCampaigns: NaverProviderCampaign[];
  startDate: Dayjs;
  endDate: Dayjs;
};

type CampaignMetrics = {
  postbackSuccess: number;
  revenue: number;
};

type PivotRow = {
  date: string;
  totalRevenue: number;
  providerTotal: Record<string, number>;
  campaign: Record<string, Record<string, CampaignMetrics>>;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);

const formatNumber = (value: number) => value.toLocaleString('ko-KR');

export default function NaverRevenueTable({ data, providerCampaigns, startDate, endDate }: Props) {
  const tableRef = useRef<HTMLTableElement>(null);

  const filename = useMemo(
    () => `naver_revenue_${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}`,
    [startDate, endDate]
  );

  // 공급사별 캠페인 목록 (마스터 목록 기준, 데이터 유무와 무관하게 항상 전체 표시)
  const campaignsByProvider = useMemo(() => {
    const map = new Map<string, Set<string>>();
    providerCampaigns.forEach((row) => {
      const campaigns = map.get(row.providerID) ?? new Set<string>();
      campaigns.add(row.campaignType);
      map.set(row.providerID, campaigns);
    });
    const result = new Map<string, string[]>();
    map.forEach((set, provider) => result.set(provider, Array.from(set).sort()));
    return result;
  }, [providerCampaigns]);

  const providers = useMemo(
    () => Array.from(campaignsByProvider.keys()).sort(),
    [campaignsByProvider]
  );

  // 날짜별 피벗: 총매출 + 공급사별 총매출 + 공급사>캠페인별 포스트백/매출
  const pivotData = useMemo<PivotRow[]>(() => {
    const map = new Map<string, PivotRow>();
    Object.entries(data.totalRevenue).forEach(([date, totalRevenue]) => {
      map.set(date, {
        date,
        totalRevenue,
        providerTotal: data.providerRevenue[date] ?? {},
        campaign: {},
      });
    });
    data.campaignRevenue.forEach((row) => {
      const entry = map.get(row.date);
      if (!entry) return;
      if (!entry.campaign[row.providerID]) entry.campaign[row.providerID] = {};
      entry.campaign[row.providerID][row.campaignType] = {
        postbackSuccess: row.postbackSuccess,
        revenue: row.revenue,
      };
    });
    return Array.from(map.values());
  }, [data]);

  const { sorted, sortDirection, toggleSort } = useDateSort(pivotData);

  const cellSx = { borderLeft: '1px solid #e2e8f0' };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu tableRef={tableRef} filename={filename} />
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table ref={tableRef} size="small" sx={{ whiteSpace: 'nowrap' }}>
          <TableHead>
            {/* 1행: 날짜 + 총매출 + 공급사명 (colspan) */}
            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
              <TableCell rowSpan={3} sx={{ verticalAlign: 'middle', fontWeight: 700 }}>
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  날짜
                </TableSortLabel>
              </TableCell>
              <TableCell rowSpan={3} align="right" sx={{ verticalAlign: 'middle', fontWeight: 700 }}>
                총 매출
              </TableCell>
              {providers.map((p) => {
                const campaigns = campaignsByProvider.get(p) ?? [];
                return (
                  <TableCell
                    key={p}
                    colSpan={campaigns.length * 2 + 1}
                    align="center"
                    sx={{ ...cellSx, fontWeight: 700 }}
                  >
                    {p}
                  </TableCell>
                );
              })}
            </TableRow>
            {/* 2행: 공급사 총매출 + 캠페인명 (colspan) */}
            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
              {providers.map((p) => {
                const campaigns = campaignsByProvider.get(p) ?? [];
                return (
                  <Fragment key={p}>
                    <TableCell
                      rowSpan={2}
                      align="right"
                      sx={{ ...cellSx, verticalAlign: 'middle', fontWeight: 700 }}
                    >
                      공급사 매출
                    </TableCell>
                    {campaigns.map((c) => (
                      <TableCell key={`${p}-${c}`} colSpan={2} align="center" sx={{ ...cellSx, fontWeight: 700 }}>
                        {getNaverCampaignLabel(c)}
                      </TableCell>
                    ))}
                  </Fragment>
                );
              })}
            </TableRow>
            {/* 3행: 캠페인별 지표명 */}
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              {providers.map((p) =>
                (campaignsByProvider.get(p) ?? []).map((c) => (
                  <Fragment key={`${p}-${c}`}>
                    <TableCell align="right" sx={cellSx}>포스트백 성공</TableCell>
                    <TableCell align="right">매출</TableCell>
                  </Fragment>
                ))
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.date} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                <TableCell>{row.date}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(row.totalRevenue)}</TableCell>
                {providers.map((p) => {
                  const campaigns = campaignsByProvider.get(p) ?? [];
                  const providerCampaigns = row.campaign[p] ?? {};
                  return (
                    <Fragment key={p}>
                      <TableCell align="right" sx={{ ...cellSx, fontWeight: 700 }}>
                        {row.providerTotal[p] != null ? formatCurrency(row.providerTotal[p]) : '-'}
                      </TableCell>
                      {campaigns.map((c) => {
                        const metrics = providerCampaigns[c];
                        return (
                          <Fragment key={`${p}-${c}`}>
                            <TableCell align="right" sx={cellSx}>
                              {metrics ? formatNumber(metrics.postbackSuccess) : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#64748b' }}>
                              {metrics ? formatCurrency(metrics.revenue) : '-'}
                            </TableCell>
                          </Fragment>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
